import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { X } from "lucide-react";
import { CrewMember, CrewRole, CREW_ROLES } from "@/types/crew";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/components/auth/AuthProvider";

interface CrewMemberFormProps {
  onClose: () => void;
  onSubmit: (member: Partial<CrewMember>) => void;
  initialData?: CrewMember;
  projectId: string;
}

export default function CrewMemberForm({ onClose, onSubmit, initialData, projectId }: CrewMemberFormProps) {
  const [name, setName] = useState(initialData?.name || "");
  const [role, setRole] = useState<CrewRole>(initialData?.role || "Other");
  const [phone, setPhone] = useState(initialData?.phone || "");
  const [email, setEmail] = useState(initialData?.email || "");
  const [notes, setNotes] = useState(initialData?.notes || "");
  const { user } = useAuth();

  const { data: contacts } = useQuery({
    queryKey: ["crew-contacts", user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("crew_contacts")
        .select("*")
        .eq("user_id", user?.id)
        .order("name");

      if (error) throw error;
      return data;
    },
    enabled: !!user,
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({
      project_id: projectId,
      name,
      role,
      phone,
      email,
      notes,
    });
  };

  const handleContactSelect = (contactId: string) => {
    const contact = contacts?.find(c => c.id === contactId);
    if (contact) {
      setName(contact.name);
      // Ensure the role is a valid CrewRole
      const contactRole = contact.role as CrewRole;
      setRole(CREW_ROLES.includes(contactRole) ? contactRole : "Other");
      setPhone(contact.phone || "");
      setEmail(contact.email || "");
      setNotes(contact.notes || "");
    }
  };

  return (
    <div className="bg-background p-6 rounded-lg shadow-lg max-w-md w-full">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-semibold">
          {initialData ? "Edit Crew Member" : "Add Crew Member"}
        </h2>
        <Button variant="ghost" size="icon" onClick={onClose}>
          <X className="h-4 w-4" />
        </Button>
      </div>

      {contacts && contacts.length > 0 && (
        <div className="mb-4">
          <Label>Select from contacts</Label>
          <Select onValueChange={handleContactSelect}>
            <SelectTrigger>
              <SelectValue placeholder="Choose a contact" />
            </SelectTrigger>
            <SelectContent>
              {contacts.map((contact) => (
                <SelectItem key={contact.id} value={contact.id}>
                  {contact.name} - {contact.role}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="name">Name</Label>
          <Input
            id="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="role">Role</Label>
          <Select value={role} onValueChange={(value) => setRole(value as CrewRole)}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {CREW_ROLES.map((role) => (
                <SelectItem key={role} value={role}>
                  {role}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="phone">Phone (optional)</Label>
          <Input
            id="phone"
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="email">Email (optional)</Label>
          <Input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="notes">Notes (optional)</Label>
          <Textarea
            id="notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </div>

        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit">
            {initialData ? "Save Changes" : "Add Member"}
          </Button>
        </div>
      </form>
    </div>
  );
}
